/**
 * `useSagaRun` — phase 21 polling hook.
 *
 * Polls `GET /api/saga-runs/:run_id` every 1000 ms until the run
 * reaches a terminal state (`done` / `failed`) OR the cap of 60 polls
 * is exhausted (60-second timeout per locked sub-decision B).
 *
 * Returns:
 *   - `state: 'in_progress' | 'done' | 'failed' | 'timeout' | 'forbidden'`
 *   - `last_error: string | null` (failed / forbidden only)
 *   - `attempts: integer`
 *   - `started_at: ISO-8601`
 *   - `terminated_at: ISO-8601 | null`
 *
 * A non-2xx poll is retried ONLY when retrying can change the answer: 404
 * (the emit's INSERT may race the first poll in test contexts) and 5xx
 * (transient). 401/403 are deterministic — the session or a permission
 * refused the READ, and sixty more polls would render a hard refusal as
 * "timed out", which is how a page-gated `/api/saga-runs/` turned every
 * restricted-staff booking into a fake 60s hang (2026-08-18). Those stop
 * immediately with `state: 'forbidden'` and the server's code in
 * `last_error`; the action itself was already dispatched and is unaffected.
 *
 * Stops on unmount (cleanup cancels the next scheduled poll).
 *
 * Authorises through the SHARED `bearerToken()` — the capability token when a
 * capability view is on screen, the Supabase session otherwise. It used to read
 * the session itself, which is the third time that rule was written out by
 * hand and the third bug it caused: the framed member page has no Supabase
 * session, so the poll went out with an EMPTY Authorization header and came
 * back 401. The dispatch had already succeeded, so a cancel ran and then
 * reported "this account was refused permission to check its progress
 * (whoami_unauthorized)", and a refund landed without the page ever refreshing
 * — the poll never reached `done`, so the `onDone` refetch never fired
 * (Mario, 2026-09-12). `/api/saga-runs/` is page-gate exempt by design, so the
 * token is the only thing that was missing.
 */

import { useEffect, useRef, useState } from "react";
import { bearerToken } from "./capability";

export const POLL_INTERVAL_MS = 1000;
export const POLL_MAX_COUNT = 60;

export interface SagaRunState {
  readonly state: "in_progress" | "done" | "failed" | "timeout" | "forbidden";
  readonly run_id: string | null;
  readonly action: string | null;
  readonly saga_class: string | null;
  readonly last_error: string | null;
  readonly attempts: number;
  readonly started_at: string | null;
  readonly terminated_at: string | null;
}

/**
 * Is this poll failure final? 401/403 are decided by who the caller IS, not
 * by when they ask — no number of retries changes the answer, and burning
 * the poll budget on them reports a permission problem as a timeout.
 */
export function isFatalPollStatus(status: number): boolean {
  return status === 401 || status === 403;
}

const INITIAL_STATE: SagaRunState = {
  state: "in_progress",
  run_id: null,
  action: null,
  saga_class: null,
  last_error: null,
  attempts: 0,
  started_at: null,
  terminated_at: null,
};

/**
 * Poll `/api/saga-runs/:run_id`. Pass `null` to disable polling (e.g.
 * before the wrapper has been dispatched + returned a run_id).
 */
export function useSagaRun(runId: string | null): SagaRunState {
  const [state, setState] = useState<SagaRunState>(INITIAL_STATE);
  // Use a ref so we don't restart polling on every render.
  const pollCountRef = useRef<number>(0);
  const cancelledRef = useRef<boolean>(false);

  useEffect(() => {
    cancelledRef.current = false;
    pollCountRef.current = 0;
    if (runId === null) {
      setState(INITIAL_STATE);
      return () => {
        cancelledRef.current = true;
      };
    }
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const tick = async (): Promise<void> => {
      if (cancelledRef.current) return;
      pollCountRef.current += 1;
      try {
        const token = await bearerToken();
        const res = await fetch(`/api/saga-runs/${runId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (cancelledRef.current) return;
          if (isFatalPollStatus(res.status)) {
            // Auth/permission refusal — deterministic, so retrying only
            // converts it into a misleading "timeout". Surface it.
            let code = `HTTP ${res.status}`;
            try {
              const body = (await res.json()) as { code?: unknown };
              if (typeof body.code === "string") code = body.code;
            } catch {
              // Non-JSON body — keep the status-line code.
            }
            setState((s) => ({ ...s, state: "forbidden", last_error: code }));
            return;
          }
          // 404 → run not found yet (the emit's INSERT is committed
          // before the inngest.send fires, but in test contexts we may
          // race); 5xx → transient. Keep polling until the cap.
          if (pollCountRef.current >= POLL_MAX_COUNT) {
            setState((s) => ({ ...s, state: "timeout" }));
            return;
          }
          timeoutHandle = setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        const body = (await res.json()) as {
          run_id: string;
          action: string;
          saga_class: string;
          terminal_state: "in_progress" | "done" | "failed";
          last_error: string | null;
          attempts: number;
          started_at: string;
          terminated_at: string | null;
        };
        if (cancelledRef.current) return;
        const next: SagaRunState = {
          state: body.terminal_state,
          run_id: body.run_id,
          action: body.action,
          saga_class: body.saga_class,
          last_error: body.last_error,
          attempts: body.attempts,
          started_at: body.started_at,
          terminated_at: body.terminated_at,
        };
        setState(next);
        if (
          body.terminal_state === "done" ||
          body.terminal_state === "failed"
        ) {
          return;
        }
        if (pollCountRef.current >= POLL_MAX_COUNT) {
          setState((s) => ({ ...s, state: "timeout" }));
          return;
        }
        timeoutHandle = setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        // Network / parse error — keep polling until the cap.
        if (cancelledRef.current) return;
        if (pollCountRef.current >= POLL_MAX_COUNT) {
          setState((s) => ({ ...s, state: "timeout" }));
          return;
        }
        timeoutHandle = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    void tick();
    return () => {
      cancelledRef.current = true;
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
    };
  }, [runId]);

  return state;
}
